import { useContext, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { DRAGNDROP_TYPE } from 'enums'
import { Context as DnDContext } from '~/components/Timeline/DnDContextProvider'
import { DraggingItemContext } from '~/components/Timeline/DraggingItemProvider'
import { TimelineTopOffsetContext } from '~/components/Timeline/TimelineTopOffsetContext'
import { TimelineScrollPositionContext } from '~/components/Timeline/ScrollPositionContext'
import { getAssetsByLayers, getDnDPatchAssets, getSourceFilesByIds, getSelectedAssetsByIds } from '~/selectors'
import { TransitionAsset } from '~/models/Asset'
import { assetToTimelineDragElement } from './assetToTimelineDragElement'
import { getLayerIndexFromY } from '~/Util/timeline-coords'
import { patchDnDItemAssets } from './patchDnDAssets'
import { time2Pixel } from '~/Util'
import { TimelineLeftOffsetContext } from '~/contexts/TimelineLeftOffsetContext'


export const useInitialDragData = ({ clientOffset, localTime, initialDragData }) => {
  // remove folder type items
  const item = useMemo(() => (
    initialDragData.type === DRAGNDROP_TYPE.GROUP
      ? {
        ...initialDragData,
        selectedItems: initialDragData.selectedItems
          .filter(elem => elem.type !== DRAGNDROP_TYPE.FOLDER),
      }
      : initialDragData
  ), [ initialDragData ])
  const { isDraggingItemOverLayer } = useContext(DnDContext)
  const { getProps: getDraggingItemProps } = useContext(DraggingItemContext)
  const topOffset = useContext(TimelineTopOffsetContext)
  const { timelineLeftOffset } = useContext(TimelineLeftOffsetContext)
  const { scrollLeft, scrollTop } = useContext(TimelineScrollPositionContext)

  const scale = useSelector(state => state.timeline.scale)
  const assetsByLayers = useSelector(getAssetsByLayers)
  const dndPatchAssets = useSelector(getDnDPatchAssets)

  // If changed of dndAssets (example: delete dissolve) in begin() callback
  const dragItem = patchDnDItemAssets(item, dndPatchAssets)
  const isExternalSource = dragItem?.type !== DRAGNDROP_TYPE.LAYER_ASSET

  const { isOverItem, detachedMode } = getDraggingItemProps()
  const sortedAssets = dragItem.selectedAssets ?? []
  const dragItemSelectedAssetsKeys = sortedAssets.map(asset => asset.id)

  const isMultipleTimelineAssets = Boolean((dragItemSelectedAssetsKeys.length > 1)
    && dragItemSelectedAssetsKeys.find(id => id === dragItem.id))
  const isMultipleSourceMediaFiles = isExternalSource && item.selectedItems?.length > 1

  const isMultipleDrag = isMultipleTimelineAssets || isMultipleSourceMediaFiles

  const multipleDragAssets = useSelector(state => isExternalSource
    ? getSourceFilesByIds(state, isMultipleDrag ? dragItem.selectedItems.map(file => file.id) : [])
    : getSelectedAssetsByIds(state, dragItem.selectedDragIds))

  const multipleSourceFilesWidth = multipleDragAssets.reduce((result, asset) => {
    const sum = result + asset.duration
    return sum
  }, 0)

  const draggableAssets = dragItem.selectedAssets ?? []
  const dragFirstItemStart = draggableAssets[0]?.startTime
  const dragLastItemEnd = draggableAssets[draggableAssets.length - 1]?.endTime
  const draggableBlockDuration = isExternalSource
    ? multipleSourceFilesWidth
    : dragLastItemEnd - dragFirstItemStart

  const restElementsByLayers = Array.from(
    assetsByLayers,
    ([ , value ]) => value
      .filter(a => isMultipleDrag
        ? !dragItem.selectedDragIds?.includes(a.id) && !(a instanceof TransitionAsset)
        : dragItem.id !== a.id && !(a instanceof TransitionAsset))
      .map(assetToTimelineDragElement(scale))
  )

  const targetLayerIndex = getLayerIndexFromY({
    y: clientOffset.y + scrollTop,
    topOffset,
    layersLength: restElementsByLayers.length,
  })

  return {
    isMultipleDrag,
    isDraggingItemOverLayer,
    isOverItem,
    detachedMode,
    scale,
    timelineTopOffset: topOffset,
    timelineLeftOffset,
    scrollLeft,
    scrollTop,
    targetLayerIndex,
    restElementsByLayers,
    dragItem,
    multipleDragAssets,
    dragAssetsWidth: time2Pixel(draggableBlockDuration, scale),
    draggableBlockDuration,
    sliderTimeX: time2Pixel(localTime, scale) + timelineLeftOffset - scrollLeft,
    isExternalSource,
    isMultipleSourceMediaFiles,
  }
}
